import Head from 'next/head';
import Script from 'next/script';
import { useState } from 'react';

import { ContentContainer, RichText, Spinner, Text } from '@ahc-digital/component-library';
import { RichText as JSSRichText, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { PromptForMissingDatasource } from 'src/shared/PromptForMissingDatasource';
import { GetSearchSources } from 'src/shared/SearchUI.service';
import { SwitchToPreviewMessage } from 'src/shared/SwitchToPreviewMessage';
import { translationClasses } from 'src/shared/TranslationClasses';
import { CreateS09NewsListingModel } from './S09NewsListing.service';
import { S09NewsListingProps } from './S09NewsListing.types';
import { PromptForErrorMessage } from 'src/shared/PromptForErrorMessage';
import { useCustomSitecoreContext } from 'src/shared/useCustomSitecoreContext';
import { useCreateMarketingRenderingHame } from 'src/shared/useCreateMarketingRenderingName';

export const Default = (props: S09NewsListingProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const context = useCustomSitecoreContext();
  const disableElasticSearch = context?.disableElasticSearch?.value;
  const isEditing = sitecoreContext?.pageEditing ?? false;
  const [isConfigLoading, setIsConfigLoading] = useState(true);
  const [isUiLoading, setIsUiLoading] = useState(true);
  const marketingRenderingName = useCreateMarketingRenderingHame(
    props?.rendering?.componentName as string
  );

  if (!props?.rendering?.dataSource) {
    return <PromptForMissingDatasource rendering={props?.rendering} />;
  }

  const model = CreateS09NewsListingModel(props);

  const { config, css, ui } = GetSearchSources();

  const renderingName = 'S09 - News Listing';

  if (!model.site) {
    return <PromptForErrorMessage componentName={renderingName} message="Site name not found" />;
  }

  if (!model.engine) {
    return (
      <PromptForErrorMessage componentName={renderingName} message="Search engine not found" />
    );
  }

  if (isEditing) {
    return (
      <SwitchToPreviewMessage componentName={props?.rendering.componentName}>
        <ContentContainer
          spacing="40-24"
          supportCopyAlignment="left"
          supportCopy={
            <>
              <h4>Configuration Details:</h4>
              <p>
                <b>Site:</b> {model.site} <br />
                <b>Engine:</b> {model.engine} <br />
              </p>
            </>
          }
        >
          <Text tag="h4">Editable Fields:</Text>
          <br />
          <Text tag="p" fontStyle="smParagraphBold">
            No results text:
          </Text>
          <RichText>
            <JSSRichText field={props?.fields?.NoResultsText} />
          </RichText>
          <br />
          <Text tag="p" fontStyle="smParagraphBold">
            Scope search to section:
          </Text>
          <Text tag="p">
            Click the pencil icon to select a site section to narrow results down.
          </Text>
        </ContentContainer>
      </SwitchToPreviewMessage>
    );
  }

  if (disableElasticSearch) {
    return <></>;
  }

  return (
    <>
      <Head>
        <link rel="stylesheet" key="search-solution-styles" href={css} />
      </Head>
      <Script
        src={config}
        strategy="afterInteractive"
        onReady={() => {
          setIsConfigLoading(false);
        }}
      />
      {!isConfigLoading && (
        <Script
          type="module"
          src={ui}
          strategy="afterInteractive"
          onReady={() => {
            window.AHC_ENV?.bindComponents?.();
            setIsUiLoading(false);
          }}
        />
      )}
      {isUiLoading && (
        <ContentContainer>
          <Spinner />
        </ContentContainer>
      )}
      <div
        data-react-component="NewsSearch"
        data-site={model.site}
        data-engine={model.engine}
        data-model={JSON.stringify(model.dataModel)}
        data-section={JSON.stringify(model.section)}
        className={translationClasses(props?.params)}
        id={props?.params?.AnchorLinkTargetName}
        data-component={marketingRenderingName}
      />
    </>
  );
};
